import { autoinject, computedFrom, transient, Disposable } from "aurelia-framework";
import { ClaimsRequireTypeEnum } from "../../../../models/basics/basics.models";
import { EntityTypes, type TabModel } from "@rightship-tech/common-ui/models/basic.models";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { Reflection } from "../../../../utilities/reflection";
import { VesselDetails } from "./steps/vessel-details";
import { VesselCiiRating } from "./steps/vessel-cii-rating/vessel-cii-rating";
import { VesselOverview } from "./steps/vessel-overview";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import {
    GetBannerResponse,
    RatingsSummaryModel,
    GetVesselResponse,
    VesselDetailModel,
    BannerGrouping,
    VesselMousEnum,
    SystemRuleResultSummaryModel,
    CompCorrespondenceModel,
    VesselCertificateModel,
} from "../../../../models/vessels/vessel.models";
import { VesselInternalDocuments } from "./steps/documents/vessel-internal-documents";
import { I18N } from "aurelia-i18n";
import { VesselActivities } from "../../admin/activities/vessel-activity";
import { MenuItem } from "../../../components/menu/menu-item";
import { activationStrategy, Router, RouterEvent } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { EventAggregator } from "aurelia-event-aggregator";
import { CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { VesselSiblings } from "./steps/vessel-siblings";
import { VesselLists } from "./steps/vessel-lists";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { VesselSafetyScore } from "./steps/vessel-safety-score";
import { CoachmarkClientService } from "../../../../services/coachmark/coachmark-client.service";
import { VesselGhgRating } from "./steps/vessel-ghg-rating";
import * as Enumerable from "linq";
import { Correspondence } from "../common-steps/correspondence/correspondence";
import { RightShipInspectionType } from "../../../../models/rightship-inspections/rightship-inspections.models";
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { CompSafetyScore } from "@rightship-tech/common-ui/components/comp-safety-score/comp-safety-score";
import { VesselFbrPerformance } from "./steps/vessel-fbr-performance";
import * as moment from "moment";
import { VettingType } from "../../../../models/vetting/vetting.models";
import { PartialTabbedEntityItem } from "@rightship-tech/common-ui/partials/partial-tabbed-entity-item/partial-tabbed-entity-item";
import { Messages } from "../../../../configuration/event-messages.constants";
import { Messages as MessagesCommonUi } from "@rightship-tech/common-ui/constants/event-messages.constants";
import { RightshipConfirmationDialogModel } from "../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { ManageRightShipUsersClientService } from "../../../../services/modules/righship-qi-manage-users.service";
import { SubscriptionProductCodes } from '../../../../models/customers/customer-subscription.models';
import { PlatformFeatureKeys } from "@rightship-tech/common-ui/dialogs/upgrade-from-freemium/freemium-feature-info-model";
import { ZeroHarmPartnersClientService } from "../../../../services/modules/zeroharm-partners.service";
import { Pillar, ZeroHarmPartnerModel } from "../../../../models/manage/zeroharmPartner.models";
import { CustomerService } from "../../../../services/modules/customer-client.service";
import { Insights } from "../rightship-insights/common/insights/insights";
import { SafetyRatingTypeEnum } from '@rightship-tech/common-ui/models/vessels/vessel.models';
import { SubtasksService } from "../../../../services/subtasks/subtasks.service";
import { VesselDocuments } from "./steps/documents/vessel-documents";
import { DocumentRequirementApi } from "@rightship-tech/document-management-app/apis/document-requirement.api";
import { VesselMessageCentre } from "./steps/vessel-message-centre/vessel-message-centre";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
@transient()
export class Vessel {

    private _canRequestVet: boolean;
    private _canRequestAutoScreening: boolean;
    private _subscriptions: Disposable[] = [];
    private canViewVesselCorrespondence: boolean;
    private rightShipUser: boolean;

    public shouldRemoveCacheOnDetached: boolean = true;
    public viewModelInCompose: any = null;
    public modelInCompose: any = null;
    public vessel: VesselDetailModel;
    public pandIClubName: string;
    public isInMyFleet: boolean;
    public entityType: number = EntityTypes.Vessel;
    public currentTab: TabModel = null;
    public currentTabKey: string = "vessel-tabs";
    public groupedBanners: BannerGrouping[] = [];
    public canEditVesselDetails: boolean;
    public canAddMOCForms: boolean = false;
    public canAddClassSurveyStatusReportForms: boolean = false
    public canRequestRightShipInspection: boolean;
    public menuItems: MenuItem[] = [];
    public canAddToMyFleet: boolean;
    public canRemoveFromMyFleet: boolean;
    public canAccessDocumentTabInVessels: boolean;
    public canViewVesselInternalDocuments: boolean;
    public canRunSystemRules: boolean;
    public canViewVesselLists: boolean;
    public canViewSisterVessels: boolean;
    public canAccessFbrPerformance: boolean;
    public canAccessVesselOverview: boolean;
    public canAccessVesselDetails: boolean;
    public canAccessVesselActivities: boolean;
    public canAddPsc: boolean;
    public canRequireRightShipInspection: boolean;
    public canCreateCargillInspection: boolean;
    public canCreateWetInspection: boolean;
    public canAddEnvironmentalVerification: boolean;
    public canRequireEnvironmentalVerification: boolean;
    public canMergeVessels: boolean;
    public canAddIncident: boolean;
    public vesselId: number;
    public ratingModel: RatingsSummaryModel;
    public canAccessVesselGHGRating: boolean;
    public canAccessVesselCiiRating: boolean;
    public canAccessVesselInsights: boolean;
    public showSafetyScore: boolean;
    public canClearVesselMous: boolean;
    public canRequestSafetyScoreReview: boolean;
    public canViewSanctionedVessel: boolean;
    public vesselLastVisitedTabCacheKey: string = "vessel-last-visited-tab";
    public isRightStoreEnabled: boolean = false;
    public isLimitedSubscription: boolean;
    public isSanctioned: boolean;
    public isFlagFake: boolean;
    public isCustomer: boolean;
    public customerVesselIds: number[];
    public maskData: boolean;
    public zeroHarmPillars: Pillar[] = []
    public potentialPartners: ZeroHarmPartnerModel[] = []
    public customerType: string;
    public crewWelfareStatus: string;
    public crewWelfareValidUntil: Date;
    public isLegacyCrewWelfare: boolean;
    public crewWelfareGuid: string;
    public certificates: VesselCertificateModel[];
    public vesselDocumentCount: number;
    public learnMoreUrl: string;
    public isAffectedFromLoweringAgeInspection: boolean;

    @computedFrom("isSanctioned", "vesselOverview", "vesselOverview.anyS74", "isFlagFake")
    public get disableRequestNow(): boolean {
        return this.isSanctioned || this.isFlagFake || this.vesselOverview.anyS74;
    }

    @computedFrom("_canRequestAutoScreening", "_canRequestVet", "isRightStoreEnabled")
    public get requestNowIsVisible(): boolean {
        return this._canRequestAutoScreening || this._canRequestVet || this.isRightStoreEnabled;
    }

    /* Currently displayed tab */
    public tabs: TabModel[] = [];
    public entityItem: PartialTabbedEntityItem;
    //used when step is set in url/ routed to specific step
    public initialStep: string;

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    showChooseDialog() {
        this.manageVesselService.showChooseDialog({
            vessel: <VesselDetailModel>{
                vetRequiresMoreInformationEmails: this.vessel.vetRequiresMoreInformationEmails,
                documentOfComplianceEmail: this.vessel.documentOfComplianceEmail,
                cargoTypeCategoryId: this.vessel.cargoTypeCategoryId,
                vesselId: this.vessel.vesselId,
                internationalMaritimeOrganizationNumber: this.vessel.internationalMaritimeOrganizationNumber,
                name: this.vessel.name
            },
            onConfirm: this.scrollToTop,
            groupedBanners: this.groupedBanners
        });
    }

    requestAutoScreening() {
        this.manageVesselService.showChooseDialog({
            vessel: <VesselDetailModel>{
                vetRequiresMoreInformationEmails: this.vessel.vetRequiresMoreInformationEmails,
                documentOfComplianceEmail: this.vessel.documentOfComplianceEmail,
                cargoTypeCategoryId: this.vessel.cargoTypeCategoryId,
                vesselId: this.vessel.vesselId,
                internationalMaritimeOrganizationNumber: this.vessel.internationalMaritimeOrganizationNumber,
                name: this.vessel.name
            },
            vettingType: VettingType.AutoScreening,
            onConfirm: this.scrollToTop,
            groupedBanners: this.groupedBanners
        });
    }

    requestVet() {
        this.manageVesselService.showChooseDialog({
            vessel: <VesselDetailModel>{
                vetRequiresMoreInformationEmails: this.vessel.vetRequiresMoreInformationEmails,
                documentOfComplianceEmail: this.vessel.documentOfComplianceEmail,
                cargoTypeCategoryId: this.vessel.cargoTypeCategoryId,
                vesselId: this.vessel.vesselId,
                internationalMaritimeOrganizationNumber: this.vessel.internationalMaritimeOrganizationNumber,
                name: this.vessel.name
            },
            vettingType: VettingType.FullVet,
            onConfirm: this.scrollToTop,
            groupedBanners: this.groupedBanners
        });
    }

    currentVm: Vessel = this;

    @computedFrom('menuItems', 'menuItems.length', 'vessel', 'vessel.vesselId')
    get isMenuVisible() {
        return this.vessel && this.vessel.vesselId && this.menuItems && this.menuItems.filter(x => typeof x.isVisible === "function" ? x.isVisible() : x.isVisible).length > 0;
    }

    @computedFrom("vessel.internationalMaritimeOrganizationNumber", "vessel.vehicleIdentificationNumber", "vessel.europeanNumberOfIdentification")
    public get vesselIdentifier(): string {
        if (this.vessel.internationalMaritimeOrganizationNumber) {
            return ` (${this.vessel.internationalMaritimeOrganizationNumber})`;
        }
        else if (this.vessel.vehicleIdentificationNumber) {
            return ` (${this.vessel.vehicleIdentificationNumber})`;
        }
        else if (this.vessel.europeanNumberOfIdentification) {
            return ` (${this.vessel.europeanNumberOfIdentification})`;
        }
        return '';
    }

    public compSafetyScore: CompSafetyScore;

    @computedFrom("compSafetyScore", "compSafetyScore.daysRemaining", "vessel", "vessel.age", "vessel.dwt")
    public get rsInspectionRequired() {
        return ((this.compSafetyScore && (this.compSafetyScore.daysRemaining == null || this.compSafetyScore.daysRemaining >= 0)) && ((this.vessel.age > 14 && this.vessel.dwt > 8000) ||
            this.vessel.age > 25))
    }

    @computedFrom(Reflection.getPropertyPath<Vessel>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        let title = this.router.history.getState("previousRouteName");

        if (title && title.trim() != "" && title != this.i18N.tr('back-text'))
            return title;

        const prevInstruction = this.router.currentInstruction.previousInstruction;
        title = prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');

        this.router.history.setState("previousRouteName", title);

        return title;
    }

    @computedFrom("rightShipUser", "isSanctioned", "isFlagFake")
    private get shouldLimitSanctionedVesselAccess(): boolean {
        return !this.rightShipUser && (this.isSanctioned || this.isFlagFake);
    }

    @computedFrom(Reflection.getPropertyPath<Vessel>(z => z._canRequestVet))
    get canRequestVet(): boolean {
        return this._canRequestVet;
    }

    @computedFrom(Reflection.getPropertyPath<Vessel>(z => z._canRequestAutoScreening))
    get canRequestAutoScreening(): boolean {
        return this._canRequestAutoScreening;
    }

    constructor(private router: Router,
        private manageVesselService: ManageVesselService,
        private internalDocuments: VesselInternalDocuments,
        private documents: VesselDocuments,
        private vesselLists: VesselLists,
        private clipboardCopyService: ClipboardCopyService,
        private signalClientService: SignalClientService,
        private vesselOverview: VesselOverview,
        private vesselSafetyScore: VesselSafetyScore,
        private vesselGhgRating: VesselGhgRating,
        private details: VesselDetails,
        private vesselCiiRating: VesselCiiRating,
        private insights: Insights,
        private userClaimsService: UserClaimsService,
        private notificationClientService: NotificationClientService,
        private dialogService: DialogClientService,
        private i18N: I18N,
        private vesselSiblings: VesselSiblings,
        private vesselFbrPerformance: VesselFbrPerformance,
        private correspondence: Correspondence,
        private vesselMessageCentre: VesselMessageCentre,
        private vesselActivity: VesselActivities,
        private entityComments: EntityComments,
        private eventAggregator: EventAggregator,
        private cacheClient: CacheClientService,
        private coachmarkClientService: CoachmarkClientService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private manageRightShipUsersClientService: ManageRightShipUsersClientService,
        private dialogClientService: DialogClientService,
        private zeroHarmPartnerService: ZeroHarmPartnersClientService,
        private customerService: CustomerService,
        private subtaskService: SubtasksService,
        private documentRequirementApi: DocumentRequirementApi,
        private userClientService: UserClientService,
        private configService: ConfigService) {
        this.vesselActivity.isMenuVisible = () => this.isMenuVisible;
        this.details.vesselDetailsDetails.isMenuVisible = () => this.isMenuVisible;
        // removed as per PX-6404
        // this.canAddToMyFleet = this.userClaimsService.hasPermission(Claims.CanAddToMyFleet) && userClientService.currentUser.settings.customerId;
        // this.canRemoveFromMyFleet = this.userClaimsService.hasPermission(Claims.CanRemoveFromMyFleet);
        this._canRequestVet = this.userClaimsService.hasPermission(Claims.CanRequestVet);
        this._canRequestAutoScreening = this.userClaimsService.hasPermission(Claims.CanRequestAutoScreening);
        this.canViewVesselInternalDocuments = this.userClaimsService.hasPermission(Claims.CanViewVesselInternalDocuments);
        this.canAccessDocumentTabInVessels = this.userClaimsService.hasPermission(Claims.CanAccessDocumentTabInVessels);
        this.canRunSystemRules = this.userClaimsService.hasPermission(Claims.CanRunSystemRules);
        this.canViewVesselLists = this.userClaimsService.hasPermission(Claims.CanViewVesselLists);
        this.canViewVesselCorrespondence = this.userClaimsService.hasPermission(Claims.CanViewVesselCorrespondence);
        this.canEditVesselDetails = userClaimsService.hasPermission(Claims.EditVesselDetails);
        this.canAddIncident = this.userClaimsService.hasPermission(Claims.CanAddIncident);
        this.canAddPsc = this.userClaimsService.hasPermission(Claims.CanAddPSC);
        this.canRequireRightShipInspection = this.userClaimsService.hasPermission(Claims.CanRequireRightShipInspection);
        this.canCreateCargillInspection = this.userClaimsService.hasPermission(Claims.CanCreateCargillInspection);
        this.canCreateWetInspection = this.userClaimsService.hasPermission(Claims.CanCreateWetInspection);
        this.canAddEnvironmentalVerification = this.userClaimsService.hasPermission(Claims.CanAddEnvironmentalVerification);
        this.canRequireEnvironmentalVerification = this.userClaimsService.hasPermission(Claims.CanRequireEnvironmentalVerification);
        this.canMergeVessels = this.userClaimsService.hasPermission(Claims.CanMergeVessels);
        this.canViewSisterVessels = this.userClaimsService.hasPermission(Claims.CanViewSisterVessels);
        this.canAccessFbrPerformance = this.userClaimsService.hasPermission(Claims.CanAccessFbrPerformance);
        this.canAccessVesselOverview = this.userClaimsService.hasPermission(Claims.CanAccessVesselOverview);
        this.canAccessVesselDetails = this.userClaimsService.hasPermission(Claims.CanAccessVesselDetails);
        this.canAccessVesselActivities = this.userClaimsService.hasPermission(Claims.CanAccessVesselActivities);
        this.canAccessVesselGHGRating = this.userClaimsService.hasPermission(Claims.CanAccessVesselGHGRating);
        this.canAccessVesselCiiRating = this.userClaimsService.hasPermission(Claims.CanAccessVesselCiiRating);
        this.canAccessVesselInsights = this.userClaimsService.hasPermission(Claims.CanAccessVesselInsights);
        this.canAddMOCForms = this.userClaimsService.hasPermission(Claims.CanAddMOCForms);
        this.canAddClassSurveyStatusReportForms = this.userClaimsService.hasPermission(Claims.CanAddClassSurveyStatusReportForms);
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canRequestRightShipInspection = this.userClaimsService.hasPermission(Claims.CanRequestRightShipInspection);
        this.showSafetyScore = this.userClaimsService.hasPermission(Claims.ShowSafetyScore);
        this.canClearVesselMous = this.userClaimsService.hasPermission(Claims.CanClearVesselMous);
        this.canRequestSafetyScoreReview = this.userClaimsService.hasPermission(Claims.CanRequestSafetyScoreReview);
        this.canViewSanctionedVessel = this.userClaimsService.hasPermission(Claims.CanViewSanctionedVessel);
        this.isRightStoreEnabled = this.userClaimsService.hasPermission(Claims.IsRightStoreEnabled);
        this.isCustomer = !userClaimsService.hasPermission(Claims.RightShipUser);
        this.learnMoreUrl = this.configService.config.urls.learnMoreUrl;
    }

    getTabs = (): TabModel[] => {
        return [
            {
                title: 'Overview',
                step: 1,
                vm: this.vesselOverview,
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessVesselOverview),
                lazyLoad: true,
                param: "overview",
                getModel: () => {
                    return {
                        vessel: this.vessel,
                        crewWelfareValidUntil: this.crewWelfareValidUntil,
                        isLegacyCrewWelfare: this.isLegacyCrewWelfare,
                        crewWelfareGuid: this.crewWelfareGuid,
                        crewWelfareStatus: this.crewWelfareStatus,
                        isSanctioned: this.isSanctioned,
                        isFlagFake: this.isFlagFake,
                        certificates: this.certificates,
                        maskData: this.maskData,
                        customerType: this.customerType || 'N/a',
                        isLimitedSubscription: this.isLimitedSubscription
                    }
                }
            },
            {
                title: 'Safety Score',
                step: 2,
                vm: this.vesselSafetyScore,
                show: () => this.showSafetyScore && this.userClaimsService.hasPermissions([
                    Claims.CanAccessSafetyScoreBenchmarking, Claims.CanAccessSafetyScoreSubscoreSummary,
                    Claims.CanAccessSafetyScoreVesselScores, Claims.CanAccessSafetyScoreDocScores,
                    Claims.CanAccessSafetyScoreFlagScores, Claims.CanAccessSafetyScoreClassScores], ClaimsRequireTypeEnum.RequireAny),
                lazyLoad: true,
                param: "safetyscore",
                class: this.shouldLimitSanctionedVesselAccess ? "vessel-safety-score disabled" : "vessel-safety-score",
                getModel: () => { return this.vessel; },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;

                    const lst = [
                        SafetyRatingTypeEnum.FullScore,
                        SafetyRatingTypeEnum.Partial,
                        SafetyRatingTypeEnum.Score1,
                        SafetyRatingTypeEnum.Score2,
                    ];

                    if (MobileHelper.isMobileDevice() && lst.indexOf(this.ratingModel.safetyRatingType) != -1) {
                        setTimeout(() => {
                            this.coachmarkClientService.start(RightShipQiRouterConstants.RouteNames.VesselDetails);
                        });
                    }
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselTabSafetyScore);
                    }
                }
            },
            {
                title: 'GHG Rating',
                step: 3,
                vm: this.vesselGhgRating,
                show: () => this.canAccessVesselGHGRating && this.vessel.canRequestGhgVerification,
                lazyLoad: true,
                param: "ghg-rating",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => {
                    return {
                        vessel: this.vessel, isSanctioned: this.isSanctioned, isFlagFake: this.isFlagFake, latestShipIndex: this.ratingModel?.latestShipIndex,
                        requestAnEnvironmentalVerification: this.requestAnEnvironmentalVerification
                    }
                },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselTabGhgRating);
                    }
                }
            },
            {
                title: 'CII Rating',
                step: 4,
                vm: this.vesselCiiRating,
                show: () => this.canAccessVesselDetails && this.canAccessVesselCiiRating,
                lazyLoad: true,
                param: "cii-rating",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () =>{
                    return {
                        vesselId: this.vesselId,
                        customerType: this.customerType || 'N/a',
                    }
                },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselTabOperationalMetrics);
                    }
                }
            },
            {
                title: 'Insights',
                step: 5,
                vm: this.insights,
                show: () => this.canAccessVesselInsights,
                lazyLoad: true,
                param: "insights",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => ({
                    entityId: this.vesselId,
                    entityTypeId: EntityTypes.Vessel,
                }),
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                }
            },
            {
                title: 'Details',
                step: 6,
                vm: this.details,
                show: () => this.canAccessVesselDetails,
                lazyLoad: true,
                param: "details",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return { vessel: this.vessel } },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselTabDetails);
                    }
                }
            },
            {
                title: 'Activities',
                step: 7,
                vm: this.vesselActivity,
                show: () => this.canAccessVesselActivities,
                lazyLoad: true,
                param: "activities",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return this.vesselActivity },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                    this.vesselActivity.initialize(this.vessel);
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselTabActivities);
                    }
                }
            },
            {
                title: "FBR Performance",
                step: 7,
                vm: this.vesselFbrPerformance,
                show: () => this.canAccessFbrPerformance,
                lazyLoad: true,
                param: "fbr-performance",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return { vesselId: this.vesselId }; },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: "Lists",
                step: 9,
                vm: this.vesselLists,
                show: () => this.canViewVesselLists,
                lazyLoad: true,
                param: "lists",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return { vesselId: this.vesselId } },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: "Sister Vessels",
                step: 10,
                vm: this.vesselSiblings,
                show: () => this.canViewSisterVessels,
                lazyLoad: true,
                param: "sister-vessels",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return { vesselId: this.vesselId, leadSisterVesselId: this.vessel.leadSisterVesselId } },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: () => { return TitleWithCountHelper.getTitle("Documents", this.vesselDocumentCount) },
                step: 10,
                vm: this.documents,
                show: () => this.canAccessDocumentTabInVessels,
                lazyLoad: true,
                param: "documents",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return this.vessel },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;


                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: () => { return TitleWithCountHelper.getTitle("Internal Documents", this.vessel.documentsCount) },
                step: 11,
                vm: this.internalDocuments,
                show: () => this.canViewVesselInternalDocuments,
                lazyLoad: true,
                param: "internal-documents",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return this.vessel },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                                
                    //Send event to google analytics
                    this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_view_docs_document_tab", {});
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: () => { return TitleWithCountHelper.getTitle("Message Centre", this.vessel.correspondenceCount) },
                step: 12,
                vm: this.vesselMessageCentre,
                show: () => this.userClaimsService.hasPermission(Claims.ShowMessageCentreInVessel),
                lazyLoad: true,
                param: "rfi-correspondence",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => {
                    return {
                        item: this.vessel,
                        email: this.userClientService.currentUser.user.email
                    };
                },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: () => { return TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.vessel.correspondenceCount) },
                step: 13,
                vm: this.correspondence,
                show: () => this.canViewVesselCorrespondence && this.vessel ? true : false,
                lazyLoad: true,
                param: "correspondence",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => {
                    return this.getCorrespondenceModel()
                },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            },
            {
                title: () => { return TitleWithCountHelper.getTitle("Notes", this.vessel.privateNotesCount) },
                step: 14,
                vm: this.entityComments,
                show: () => this.userClaimsService.hasPermission(Claims.CanViewVesselNotes),
                lazyLoad: true,
                param: "notes",
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : "",
                getModel: () => { return { entityId: this.vesselId, entityTypeId: this.vessel.entityType, item: this.vessel, canSubmit: true } },
                onClick: () => {

                    //If sanctioned, don't allow tab click
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                },
                lockedFeature: this.maskData,
                unlockFeature: () => {
                    if (this.isLimitedSubscription) {
                        this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.vesselAllTabs);
                    }
                }
            }
        ];
    }

    setCurrentStep(routeStep: string, documentRequirementId: number = null) {
        let step: TabModel = null;
        const steps = this.tabs.filter(s => this.getShow(s));
        if (routeStep != undefined) {
            step = steps.filter(s => s.param == routeStep)[0];
        }

        if (!step) {
            step = steps[0];
        }

        if (this.entityItem) {

            //If is freemium, filter the list of steps and get the first item which is not freemium
            if (step.lockedFeature) {
                step = steps.filter(x => !x.lockedFeature)[0];
            }

            //Document requirement specific logic
            step.vm.selectedRequirementId = documentRequirementId;

            this.entityItem.currentStep = step;
        }
    }

    getShow(s: TabModel) {
        return "show" in s ? (typeof (s.show) == "function" ? s.show() : s.show) : true;
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    attached() {
        if (this._subscriptions) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }

        if (this.initialStep)
            this.setCurrentStep(this.initialStep);

        this._subscriptions.push(this.eventAggregator.subscribe(RouterEvent.Processing, (x) => {
            if (x && x.instruction && x.instruction.config) {
                const name = x.instruction.config.name;
                this.shouldRemoveCacheOnDetached = name != RightShipQiRouterConstants.RouteNames.PSC && name != RightShipQiRouterConstants.RouteNames.Vet && name != RightShipQiRouterConstants.RouteNames.Incident;
                //Remove tab cahce if is navigating from another vessel
                if (name == RightShipQiRouterConstants.RouteNames.VesselDetails && name == x.instruction.previousInstruction.config.name) {
                    this.cacheClient.remove(this.currentTabKey);
                }
            }
        }));

        this._subscriptions.push(this.eventAggregator.subscribe(Messages.requestNowVHCBadgeClick, () => {
            this.showChooseDialog();
        }));

        //Change tab to message-centre
        this._subscriptions.push(this.eventAggregator.subscribe(MessagesCommonUi.changeTab, () => {
            this.setCurrentStep('message-centre');
        }));

        //Change tab to documents
        this._subscriptions.push(this.eventAggregator.subscribe(MessagesCommonUi.changeTabToDocuments, (x) => {
            this.setCurrentStep('documents', x.requirementId);
        }));

        if (!this.maskData) {
            this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        }
        this.signalClientService.subscribe(SignalFunctions.vesselsSynced, this.refreshVessel);
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
        this.signalClientService.subscribe(SignalFunctions.subtaskAdded, this.updateCorrespondenceCount);


        if (!MobileHelper.isMobileDevice()) {
            this.coachmarkClientService.start(RightShipQiRouterConstants.RouteNames.VesselDetails);
        }
    }

    detached() {

        if (this._subscriptions) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }

        if (!this.maskData) {
            this.signalClientService.unsubscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        }
        this.signalClientService.unsubscribe(SignalFunctions.vesselsSynced, this.refreshVessel);
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
        this.signalClientService.unsubscribe(SignalFunctions.subtaskAdded, this.updateCorrespondenceCount);
    }

    public onStepClicked = (step: TabModel) => {
        if (!step) {
            return;
        }

        if (step.param) {
            const params = this.router.currentInstruction.params;
            params.step = step.param;

            const state = window.history.state;

            this.router.navigateToRoute(this.router.currentInstruction.config.name, params, {
                trigger: false,
                replace: true,
            });

            window.history.replaceState(state, "Company");
        }
    }

    refreshRightShipInspectionStatus = (params: EntityStatusChangeModel) => {
        if (this.vessel && this.vessel.lastRightShipInspectionId == params.entityId) {
            this.refreshRating({
                vesselIds: [this.vesselId]
            });
        }
    }

    refreshVessel = (params) => {
        if (params && params.vesselIds && params.vesselIds.indexOf(this.vesselId) > -1) {
            this.manageVesselService.getVessel({
                vesselId: this.vesselId,
                imo: this.vessel.internationalMaritimeOrganizationNumber,
                bKey: this.vessel.bKey
            }).then(vesselResponse => {
                if (!this.vessel) {
                    this.setVesselsData(vesselResponse);
                }
                else {
                    for (const prop in vesselResponse.vessel) {
                        this.vessel[prop] = vesselResponse.vessel[prop];
                    }
                }

                this.vesselActivity.initialize(this.vessel);

                this.refreshRating({
                    vesselIds: [this.vesselId]
                });
            }).catch(e => {
                console.error("Failed To refresh vesselDetails. error: " + e.message);
            });
        }
    }

    addIncident() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddIncident,
            {
                id: 0,
                vesselId: this.vessel.vesselId,
                internationalMaritimeOrganizationNumber:
                    this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber : '0',
                vesselName: this.vessel.name,
                registeredOwnerId: this.vessel.registeredOwnerCompanyId
            });
    }

    scrollToTop() {
        const scrollElement = MobileHelper.getScrollParent(document.getElementsByClassName('page-vessel').item(0), false) as HTMLDivElement;

        if (scrollElement) {
            scrollElement.scrollTop = 0;
        }
    }

    addInspection(inspectionType) {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddRightShipInspection, {
            id: 0,
            inspectionTypeId: inspectionType,
            vesselId: this.vessel.vesselId
        });
        //pass the vessel through history instead of url
        this.router.history.setState("vessel", this.vessel);
    }

    addPsc() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddPsc,
            {
                id: 0,
                vesselId: this.vessel.vesselId,
                internationalMaritimeOrganizationNumber:
                    this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber : '0',
                vesselName: this.vessel.name
            });
    }

    requestAnEnvironmentalVerification = () => {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.RequestAnEnvironmentalVerification,
            {
                vesselId: this.vessel.vesselId
            });
    }

    requestSafetyScoreReivew() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RequestSafetyScoreReviewDialog, this.vessel, true, false, false, 'right');
    }

    addFbr() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.NewFbr, {
            vesselId: this.vessel.vesselId,
            cargoTypeCategoryId: this.vessel.cargoTypeCategoryId
        });
    }

    addCfr() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.NewCfr, {
            vesselId: this.vessel.vesselId
        });
    }

    addToList() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.AddToList,
            {
                entityId: this.vessel.vesselId,
                entityType: this.entityType,
                onComplete: this.onAddListComplete
            }, true, false, false, 'right');
    }

    // addToMyFleet() {
    //     let action = "";
    //     let promise: Promise<void> = null;
    //     if (this.isInMyFleet) {
    //         let model: RightshipConfirmationDialogModel = {
    //             title: "Remove Vessel?",
    //             messages: ["You will no longer see this vessel in your fleet list"],
    //             cancel: "Cancel",
    //             confirm: "Remove",
    //             messageClasses: [],
    //             onConfirm: () => {
    //                 action = "remove";
    //                 promise = this.myFleetService.removeFromMyFleet([this.vessel.vesselId]);
    //                 this.addRemoveMyFleet(promise, action);
    //             }
    //         };

    //         this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
    //     } else {
    //         promise = this.myFleetService.addToMyFleet([this.vessel.vesselId]);
    //         action = "add"
    //         this.addRemoveMyFleet(promise, action);
    //     }

    // }

    // private addRemoveMyFleet(promise: Promise<void>, action: string) {
    //     promise.then(() => {
    //         this.isInMyFleet = !this.isInMyFleet;
    //         this.notificationClientService.show(`${action == 'add' ? 'Added to' : 'Removed from'} my fleet`, "", NotificationType.Success);
    //     }, e => {
    //         let actionPart = action == 'add' ? 'add to' : 'remove from';
    //         if (e.responseType == "json") {
    //             let error = JSON.parse(e.response);
    //             if (error && error.key == "business-unit-not-found") {
    //                 this.notificationClientService.show(`The current user is not assigned to a Business Unit thus cannot ${actionPart} My Fleet`, "", NotificationType.Error);
    //             } else
    //                 this.notificationClientService.show(`Failed to ${actionPart} my fleet`, "", NotificationType.Error);

    //         } else {
    //             this.notificationClientService.show(`Failed to ${actionPart} my fleet`, "", NotificationType.Error);
    //         }
    //     });
    // }

    async activate(args: any) {
        if (!args || (!args.id && !args.imo && !args.bKey)) {
            return;
        }

        try {

            const vesselResponse = await this.manageVesselService.getVessel({
                vesselId: args.id,
                imo: args.imo,
                bKey: args.bKey
            });

            this.vesselId = vesselResponse.vessel?.id;
            this.setVesselsData(vesselResponse);

            this.vesselActivity.initialize(this.vessel);
            await this.refreshRating({
                vesselIds: [this.vesselId]
            });
            
            if(this.canAccessDocumentTabInVessels)
                await this.getDocumentCount();

            //Is freemium
            this.isLimitedSubscription = await this.manageRightShipUsersClientService.isLimitedCustomerSubscription(SubscriptionProductCodes.informationServices);

            const customerResponse = await this.customerService.getCurrentCustomer();

            if (this.isCustomer) {
                this.maskData = await this.manageVesselService.getMaskDataForVessel(this.vesselId);
                this.customerType = customerResponse.customers[0]?.customerType;
            }

            //#region Route Subtitle

            const imo = this.vessel.internationalMaritimeOrganizationNumber
                ? this.vessel.internationalMaritimeOrganizationNumber
                : this.vessel.vehicleIdentificationNumber
                    ? this.vessel.vehicleIdentificationNumber
                    : this.vessel.europeanNumberOfIdentification
                        ? this.vessel.europeanNumberOfIdentification
                        : 'N/A';

            const label = this.vessel.internationalMaritimeOrganizationNumber
                ? 'IMO'
                : this.vessel.europeanNumberOfIdentification
                    ? 'ENI'
                    : this.vessel.vehicleIdentificationNumber
                        ? 'VIN'
                        : 'IMO';

            await this.zeroHarmPartnerService.getZeroHarmPartnerByVesselIdentifier(this.vessel.internationalMaritimeOrganizationNumber,
                this.vessel.vehicleIdentificationNumber, this.vessel.europeanNumberOfIdentification)
                .then(x => {
                    this.zeroHarmPillars = x.pillars;
                }
                );

            await this.zeroHarmPartnerService.getPotentialPartnersByVesselIdentifier(
                this.vessel.vesselId,
                this.vessel.internationalMaritimeOrganizationNumber,
                this.vessel.vehicleIdentificationNumber, this.vessel.europeanNumberOfIdentification)
                .then(x => {
                    this.potentialPartners = x;
                }
                );

            let partnerType;
            let totalPartnerCount = 0;
            const partnersName = [];

            //Iterate through zeroHarmPillars, subCategories and zeroHarmPartners
            this.zeroHarmPillars.forEach(pillar => {
                pillar.subCategories.forEach(subCategory => {
                    subCategory.zeroHarmPartners.forEach(partner => {
                        //Increment the count for each Partner found
                        totalPartnerCount++;

                        //Add the partner name to list
                        partnersName.push(partner.name);
                    });
                });
            });

            if (totalPartnerCount > 0 && this.potentialPartners.length > 0) {
                partnerType = 'has_both_partners_and_pp';
            }
            else if (totalPartnerCount > 0 && this.potentialPartners.length <= 0) {
                partnerType = 'has_partners';
            }
            else if (totalPartnerCount <= 0 && this.potentialPartners.length > 0) {
                partnerType = 'has_potenial_partners';
            }
            else {
                partnerType = 'has_no_parters_or_pp';
            }

            this.googleAnalyticsService.sendCustomGA4EventMulti('vessel_view', {
                "action": `${this.vessel.name} ${label} ${imo}`,
                "safetyScore":
                    (this.vesselSafetyScore?.safetyScoreBenchmarking?.ratingModel?.safetyRatingType != -1 ? `${this.vesselSafetyScore?.safetyScoreBenchmarking?.ratingModel?.starRating}` : 'NA'),
                "ghgRating":
                    `${this.vesselSafetyScore?.safetyScoreBenchmarking?.ratingModel?.rating ?? 'no_ghg_rating'}${(this.vesselSafetyScore?.safetyScoreBenchmarking?.ratingModel?.ratingPlus ? '+' : '')}`,
                "partnerType": `${partnerType}`,
            });

            //GA4 custom event for a vessel view with zero harm partners
            if (totalPartnerCount > 0) {
                partnersName.forEach(partner => {
                    this.googleAnalyticsService.sendCustomGA4EventMulti('vessel_view_with_zhp', {
                        "partnerName": `${partner}`,
                        "action": `${this.vessel.name} ${label} ${imo}`,
                        "customerType": `${this.customerType  || 'N/a'}`,
                    });
                })
            }

            //GA4 custom event for a vessel view with zero harm partners
            if (this.potentialPartners.length > 0) {
                this.potentialPartners.forEach(partner => {
                    this.googleAnalyticsService.sendCustomGA4EventMulti('vessel_view_with_pp', {
                        "partnerName": `${partner.name}`,
                        "action": `${this.vessel.name} ${label} ${imo}`,
                        "customerType": `${this.customerType || 'N/a'}`,
                    });
                })
            }
            //#endregion

            this.tabs = this.getTabs();
            if (args && args.step) {
                this.initialStep = args.step;
            }

            if (this.shouldLimitSanctionedVesselAccess) {
                //if user is not rs admin and vessel is sanctioned set tab to overview
                this.currentTab = this.tabs[0];
                this.currentTab.onClick(null);
            }
            else {
                if (window.history && window.history.state && Object.keys(window.history.state).indexOf("current-tab") > -1) {
                    const step = window.history.state["current-tab"];
                    const tabs = this.tabs.filter(x => x.step == step);

                    if (tabs && tabs.length > 0) {
                        this.currentTab = tabs[0];
                        this.currentTab.onClick(null);
                    }
                }

                let currentTab = null;

                if (args.step === 'activities') {
                    currentTab = this.tabs.filter(x => x.step == 5)[0];
                }
                else if (args.step === 'ghg') {
                    currentTab = this.tabs.filter(x => x.step == 3)[0];
                }
                else if (args.step === 'safety-score') {
                    currentTab = this.tabs.filter(x => x.step == 2)[0];
                }
                else if (args.step === 'fbr') {
                    currentTab = this.tabs.filter(x => x.step == 9)[0];
                }  
                else if (args.step === 'rfi-correspondence') {
                    currentTab = this.tabs.filter(x => x.step == 12)[0];
                }

                if (currentTab) {
                    this.currentTab = currentTab;
                    if(this.currentTab.onClick)
                        this.currentTab.onClick(null);
                }
            }

            this.menuItems = [
                {
                    click: () => this.addPsc(),
                    isVisible: () => this.canAddPsc && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create port state control'
                    },
                    classList: '',
                },
                {
                    click: () => this.addIncident(),
                    isVisible: this.userClaimsService.hasPermission(Claims.CanAddIncident) && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create incident'
                    },
                    classList: '',
                },
                {
                    click: () => this.addCfr(),
                    isVisible: this.userClaimsService.hasPermission(Claims.CanAddCFR) && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create condition for review'
                    },
                    classList: '',
                },
                {
                    click: () => this.addFbr(),
                    isVisible: this.userClaimsService.hasPermission(Claims.CanAddFBR) && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create feedback report'
                    },
                    classList: '',
                },
                {
                    click: () => this.requestRightShipInspection(RightShipInspectionType.RightShipInspection),
                    isVisible: () => this.canRequireRightShipInspection && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: false,
                        removeicon: false,
                        badgeicon: true,
                        text: 'Requires RightShip inspection'
                    },
                    classList: '',
                },
                {
                    click: () => this.generateExternalGHGVerification(),
                    isVisible: () => this.canRequireEnvironmentalVerification && !(this.isSanctioned && this.isFlagFake) && this.vessel.canRequestGhgVerification,
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Requires Environmental Review'
                    },
                    classList: '',
                },
                {
                    click: () => this.requestAnEnvironmentalVerification(),
                    isVisible: () => !(this.isSanctioned && this.isFlagFake) && this.vessel.canRequestGhgVerification && this.canAddEnvironmentalVerification && this.vessel.vesselId != null
                        && (this.currentTab ? this.currentTab.step == 3 || this.currentTab.step == 12 : false),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Request an Environmental Review'
                    },
                    classList: 'request-an-environmental-verification',
                },
                // {
                //     click: () => this.requestSafetyScoreReivew(),
                //     isVisible: () => this.canRequestSafetyScoreReview && (this.ratingModel.starRating == 1 || this.ratingModel.starRating == 2),
                //     item: {
                //         addicon: true,
                //         removeicon: false,
                //         badgeicon: false,
                //         text: 'Request Safety Score Rule review'
                //     },
                //     classList: '',
                // },
                {
                    click: () => this.addInspection(RightShipInspectionType.CargillSurvey),
                    isVisible: () => this.canCreateCargillInspection && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create Cargill inspection'
                    },
                    classList: '',
                },
                {
                    click: () => this.addInspection(RightShipInspectionType.TankerInspection),
                    isVisible: () => this.canCreateWetInspection && !(this.isSanctioned && this.isFlagFake) && this.vessel.isTanker,
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create tanker inspection'
                    },
                    classList: '',
                },
                {
                    isVisible: () => this.userClaimsService.hasPermission(Claims.CanAddEntityToLists),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Add to List'
                    },
                    classList: '',
                    click: () => this.addToList(),
                },
                // {
                //     click: () => this.addToMyFleet(),
                //     isVisible: () => this.canAddToMyFleet && !this.isInMyFleet && !this.isSanctioned,
                //     item: {
                //         addicon: true,
                //         removeicon: false,
                //         badgeicon: false,
                //         text: 'Add to My Fleet'
                //     },
                //     classList: '',
                // },
                {
                    click: () => this.openFormDialog('moc'),
                    isVisible: () => this.canAddMOCForms && !this.isSanctioned,
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create MOC/VTOP'
                    },
                    classList: ''
                },
                {
                    click: () => this.openFormDialog('class-survey-status-report'),
                    isVisible: () => this.canAddClassSurveyStatusReportForms && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Create Class Status Report'
                    },
                    classList: ''
                },
                // {
                //     click: () => this.addToMyFleet(),
                //     isVisible: () => this.canRemoveFromMyFleet && this.isInMyFleet && !this.isSanctioned,
                //     item: {
                //         addicon: false,
                //         removeicon: true,
                //         badgeicon: false,
                //         text: 'Remove from My Fleet'
                //     },
                //     classList: '',
                // },
                {
                    click: () => this.openMergeVesselDialog(),
                    isVisible: () => this.canMergeVessels && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Merge with...'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.Paris),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear Paris MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.Riyadh),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear Riyadh MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.Abuja),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear Abuja MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.Caribbean),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear Caribbean MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.Tokyo),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear Tokyo MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.Amsa),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear AMSA MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.clearVesselMous(VesselMousEnum.BlackSea),
                    isVisible: () => this.canClearVesselMous && this.vessel.vesselId != null && !(this.isSanctioned && this.isFlagFake),
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Clear Black Sea MOU'
                    },
                    classList: '',
                },
                {
                    click: () => this.runSystemRules(),
                    isVisible: () => this.canRunSystemRules && this.vessel.vesselId != null,
                    item: {
                        addicon: true,
                        removeicon: false,
                        badgeicon: false,
                        text: 'Force System Rules'
                    },
                    classList: '',
                }
            ];

            setTimeout(() => {
                this.router.currentInstruction.config.title = this.vessel.name;
                this.router.currentInstruction.config.subTitle = `${label} ${imo}`;
            });

        } catch (e) {
            console.error(e);
            this.tabs = this.getTabs();
        }
    }

    private async getDocumentCount() {
        try
        {
            const vesselDocumentCount = await this.documentRequirementApi.getVesselDocumentsCount(this.vesselId);
            this.vesselDocumentCount = vesselDocumentCount;
        }
        catch (e) {
            console.warn("Failed to get document count", e);
            this.vesselDocumentCount = 0;
        }
    }

    private refreshRating = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.vesselId).length >= 1) {
            try {
                const response = await this.manageVesselService.getRatingsModel(this.vesselId);

                if ((response.isSanctioned || response.isFlagFake) && response) {
                    response.rating = "U";
                    response.score = 0;
                    // response.starRating = 0;
                    response.isVerified = false;
                    response.ratingPlus = null;
                    response.sizeScore = 0;
                    response.hasRating = false;
                }

                this.formatSystemRuleDescriptions(response.systemRuleResult)
                if (this.vesselOverview?.model?.isSanctioned) {
                    this.vesselOverview.model.isSanctioned = response.isSanctioned;
                }

                if (this.vesselOverview?.model?.isFlagFake) {
                    this.vesselOverview.model.isFlagFake = response.isFlagFake;
                }
                
                this.ratingModel = response;
                this.vesselOverview.ratingModel = response;
                this.vesselSafetyScore.safetyScoreSubscoreSummary.scoreSystemRules = response.systemRuleResult.filter(x => x.actionTypeId == response.safetyRatingType && x.actionTypeId != SafetyRatingTypeEnum.FullScore);

                this.vesselSafetyScore.safetyScoreBenchmarking.ratingModel = response;
                this.vesselSafetyScore.ratingModel = response;
                this.details.vesselDetailsDetails.ratingModel = response;
            } catch (e) {
                console.error("Failed to refresh rating", e);
            }
            this.reloadVesselBanners();
        }
    }

    private _systemRulesDescriptionPropertyMap: { placeholder: string, propertyName: string }[] = [
        { placeholder: "<@last_paris_mou_date@>", propertyName: "parisMoUVisitDate" },
        { placeholder: "<@last_abuja_mou_date@>", propertyName: "abjuaMoUVisitDate" },
        { placeholder: "<@last_rihad_mou_date@>", propertyName: "rihadMoUVisitDate" },
        { placeholder: "<@last_caribbean_mou_date@>", propertyName: "caribbeanMoUVisitDate" },
        { placeholder: "<@last_tokyo_mou_date@>", propertyName: "tokyoMoUVisitDate" },
        { placeholder: "<@last_amsa_mou_date@>", propertyName: "amsaMoUVisitDate" },
        { placeholder: "<@last_blacksea_mou_date@>", propertyName: "blackSeaMoUVisitDate" }
    ];


    private formatSystemRuleDescriptions(systemRuleResult: SystemRuleResultSummaryModel[]) {
        if (!this.vessel) return;
        this._systemRulesDescriptionPropertyMap.forEach(map => {
            systemRuleResult.filter(x => x.description.indexOf(map.placeholder) > -1)
                .forEach(x => {
                    x.description = x.description.replace(map.placeholder, this.vessel && this.vessel[map.propertyName] ? moment(this.vessel[map.propertyName]).format("DD MMM YYYY") : "N/A")
                })
        });
    }

    private onAddListComplete = () => {
        this.eventAggregator.publish("refresh-vessel-header", { vesselIds: [this.vesselId] });
        this.reloadVesselBanners();
        this.reloadLists();
        this.reloadVesselCertificates();
    }

    private reloadVesselBanners = () => {
        this.manageVesselService.getVesselBanners(this.vesselId).then(x => {
            this.details.vesselDetailsDetails.banners = x;
            const sanctioned = ['sanctioned'];
            this.isSanctioned = x.filter(x => sanctioned.indexOf(x.internalName.toLowerCase()) > -1).length > 0;
            this.isFlagFake = x.filter(x => x.isFlagFake).length > 0;

            if (this.rightShipUser && this.canViewSanctionedVessel) {
                this.isSanctioned = false;
                this.isFlagFake = false;
            }

            if (this.isSanctioned == true || this.isFlagFake == true) {
                this.tabs = this.getTabs();
            }

            if ((this.isSanctioned || this.isFlagFake) && this.ratingModel) {
                this.ratingModel.rating = "U";
                this.ratingModel.score = 0;
                this.ratingModel.starRating = 0;
                this.ratingModel.isVerified = false;
                this.ratingModel.ratingPlus = null;
                this.ratingModel.sizeScore = 0;
                this.ratingModel.hasRating = false;

                this.vesselOverview.ratingModel = this.ratingModel;
                this.vesselSafetyScore.safetyScoreBenchmarking.ratingModel = this.ratingModel;
                this.vesselSafetyScore.ratingModel = this.ratingModel;
                this.details.vesselDetailsDetails.ratingModel = this.ratingModel;

                if (this.vesselGhgRating) {
                    this.vesselGhgRating.setGhgRatingTabAsSanctioned();
                }
            }

            this.groupedBanners = [];

            Enumerable.from(x).groupBy(x => x.internalName).forEach(groupedBanner => {

                const bannerGroup = <BannerGrouping>{
                    response: groupedBanner.toArray(),
                    internalName: groupedBanner.toArray()[0].internalName,
                    displayName: groupedBanner.toArray()[0].displayName
                }

                this.groupedBanners.push(bannerGroup);
            });

        });
    }

    @computedFrom("vessel", "vessel.dateOfBuild")
    get affectedByLoweringAgeForInspection() {
        if (this.vessel.dateOfBuild) {
            const ageOf31Dec2025InMs = new Date('2025-12-31').getTime() - new Date(this.vessel.dateOfBuild).getTime();
            const ageOf31Mar2025InMs = new Date('2025-03-31').getTime() - new Date(this.vessel.dateOfBuild).getTime();

            const ageIn31Dec2025 = ageOf31Dec2025InMs / (1000 * 60 * 60 * 24 * 365.2425);
            const ageIn31Mar2025 = ageOf31Mar2025InMs / (1000 * 60 * 60 * 24 * 365.2425);

            return ageIn31Dec2025 >= 12 && ageIn31Mar2025 < 14 && this.vessel.canHaveRightShipInspection && this.configService.config.settings.enableVesselAffectedAgeChangeLabel;
        }
    }

    onBannerClick(bannerGroup: BannerGrouping) {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.BannerDescription, { bannerGroup }, true, false, false, "right");
    }

    private reloadLists = () => {
        if (this.vesselLists) {
            this.vesselLists.reload();
        }
    }

    private reloadVesselCertificates = () => {
        this.manageVesselService.getVesselCertificates(this.vesselId).then(x => {
            this.vesselOverview.certificates = x;
            this.certificates = x;
        });
    }

    getBanner(banner: GetBannerResponse) {
        return `${banner.businessUnit ? (banner.businessUnit + ' ') : ''}${banner.listName ? (banner.listName + ' ') : ''}${banner.companyName ? (banner.companyName + ' ') : ''}${banner.countryName ? (banner.countryName + ' ') : ''}`;
    }

    async runSystemRules() {
        if (this.canRunSystemRules) {
            try {
                await this.manageVesselService.rerunSystemRules([this.vessel.vesselId]);
                this.notificationClientService.show("System Rules", "Re-run system rules successfully", NotificationType.Success);
            } catch {
                this.notificationClientService.show("System Rules", "Failed to re-run system rules", NotificationType.Error);
            }
        }
    }

    requestRightShipInspection(type: number) {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RequiresRightshipInspectionDialog, {
            vesselName: this.vessel.name,
            imo: this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber :
                this.vessel.europeanNumberOfIdentification ? this.vessel.europeanNumberOfIdentification :
                    this.vessel.vehicleIdentificationNumber ? this.vessel.vehicleIdentificationNumber : "",
            type: type,
            vesselId: this.vessel.vesselId,
            id: 0,
            shipManagerEmails: this.vessel.shipManagerEmail ? [this.vessel.shipManagerEmail] : [this.vessel.documentOfComplianceEmail],
            reload: () => {
                this.refreshRating({ vesselIds: [this.vessel.vesselId] })
            }
        }, true, true, false, "right");
    }

    generateExternalGHGVerification() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.GenerateExternalGhgVerificationDialog, {
            vesselName: this.vessel.name,
            imo: this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber :
                this.vessel.europeanNumberOfIdentification ? this.vessel.europeanNumberOfIdentification :
                    this.vessel.vehicleIdentificationNumber ? this.vessel.vehicleIdentificationNumber : "",
            vesselId: this.vessel.vesselId,
            id: 0
        }, true, true, false, "right");
    }

    openMergeVesselDialog() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.MergeVesselDialog, {
            vesselName: this.vessel.name,
            imo: this.vessel.internationalMaritimeOrganizationNumber ? this.vessel.internationalMaritimeOrganizationNumber :
                this.vessel.europeanNumberOfIdentification ? this.vessel.europeanNumberOfIdentification :
                    this.vessel.vehicleIdentificationNumber ? this.vessel.vehicleIdentificationNumber : "",
            vesselId: this.vessel.vesselId,
            id: 0
        }, true, true, false, "right");
    }

    getOverriddenValuesForName(propertyName: string, overriddenValue: any, sourceComment: string) {
        return [
            { propertyName: propertyName, overriddenValue: overriddenValue, sourceComment }
        ]
    }

    public openFormDialog(formInternalName: string) {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.VesselFormDialog, {
            vesselId: this.vesselId,
            formInternalName: formInternalName
        }, true, false, false, 'right');
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    clearVesselMous(mou: VesselMousEnum = null) {
        if (this.canClearVesselMous) {

            const vesselMouName = mou != null ? this.i18N.tr(VesselMousEnum[mou]) : "";
            const model = new RightshipConfirmationDialogModel();
            model.confirm = "Yes";
            model.cancel = "No";

            model.messages.push(`Have the ${mou != null ? vesselMouName + " " : ""} MOU data been verified?`);
            model.title = "Clear MOU";

            model.onConfirm = () => {
                this.manageVesselService.clearVesselMou(this.vessel.vesselId, mou).then(x => {
                    if (x.isSuccessFull) {

                        this.subtaskService.getSubtaskByVesselMou(this.vessel.vesselId, mou).then(st => {
                            if (st != null && st.isSuccess) {
                                this.subtaskService.closeRFI(st.subtaskId, false);
                                if (st.actualSubtaskId != null)
                                    this.subtaskService.closeRFI(st.actualSubtaskId, false);
                            }
                        });

                        this.notificationClientService.show("Clear MOUs", `${mou != null ? vesselMouName + " MOU" : "MOUs"} cleared successfully`, NotificationType.Success);
                    }
                    else {
                        this.notificationClientService.show("Clear MOUs", `Failed to clear ${mou != null ? vesselMouName + " MOU" : "MOUs"}`, NotificationType.Error);
                    }
                })
                    .catch(() => {
                        this.notificationClientService.show("Clear MOUs", `Failed to clear ${mou != null ? vesselMouName + " MOU" : "MOUs"}`, NotificationType.Error);
                    })
            }


            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
        }
    }

    private getCorrespondenceModel(): CompCorrespondenceModel {
        return {
            correspondenceEntity: {
                entityId: this.vessel.vesselId,
                entityTypeId: this.vessel.entityType,
                isInconsistentState: false,
                assignment: null,
                liaseWithShipManagers: null,
                requestedBy: null
            },
            showAddRFI: false,
            name: "",
            emails: [],
            newDocumentAdded: () => {
                this.vessel.documentsCount += 1;
            }
        };
    }

    private setVesselsData(vesselResponse: GetVesselResponse) {
        this.vessel = vesselResponse.vessel;
        this.certificates = vesselResponse.certificates;
        this.crewWelfareStatus = vesselResponse.crewWelfareStatus;
        this.crewWelfareValidUntil = vesselResponse.crewWelfareValidUntil;
        this.isLegacyCrewWelfare = vesselResponse.isLegacyCrewWelfare;
        this.crewWelfareGuid = vesselResponse.crewWelfareGuid;
    }

    private updateCorrespondenceCount = (param: { vesselId: number }) => 
    {
        if(param.vesselId == this.vessel?.vesselId)
        {
            this.vessel.correspondenceCount += 1;
        }
    }

    public onLearnMoreClick() {
        window.open(this.learnMoreUrl, "_blank");
    }
}
